import React from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Text,
  Flex,
} from "@tremor/react";
import { Link } from "react-router-dom";
import { toFriendlyTime } from "../services/utils";

/**
 * Get status color and indicator based on last seen time and device type
 */
function getDeviceStatus(lastOnline, deviceType) {
  if (!lastOnline) {
    return { color: 'gray', text: 'Never' };
  }
  
  const now = new Date();
  const lastSeen = new Date(lastOnline);
  const minutesSince = (now - lastSeen) / (1000 * 60);
  
  // Different thresholds for LoRa vs MQTT devices
  if (deviceType === 'lora') {
    // LoRa devices: Online < 60min, Offline >= 60min
    if (minutesSince < 60) {
      return { color: 'green', text: 'Online' };
    } else {
      return { color: 'red', text: 'Offline' };
    }
  } else {
    // MQTT devices: Online < 5min, Recent < 60min, Offline >= 60min
    if (minutesSince < 5) {
      return { color: 'green', text: 'Online' };
    } else if (minutesSince < 60) {
      return { color: 'yellow', text: 'Recent' };
    } else {
      return { color: 'red', text: 'Offline' };
    }
  }
}

/**
 * Get activity indicator based on how recent the data is
 */
function getActivityIndicator(lastOnline) {
  if (!lastOnline) return '○○○○○○○○○○';
  
  const now = new Date();
  const lastSeen = new Date(lastOnline);
  const minutesSince = (now - lastSeen) / (1000 * 60);
  
  if (minutesSince < 2) return '●●●●●●●●●●';      // Very high
  if (minutesSince < 5) return '●●●●●●●●○○';      // High
  if (minutesSince < 15) return '●●●●●●○○○○';     // Medium-High
  if (minutesSince < 30) return '●●●●○○○○○○';     // Medium
  if (minutesSince < 60) return '●●○○○○○○○○';     // Low
  return '○○○○○○○○○○';                          // None
}

/**
 * Get activity level text
 */
function getActivityLevel(lastOnline) {
  if (!lastOnline) return 'None';
  
  const now = new Date();
  const lastSeen = new Date(lastOnline);
  const minutesSince = (now - lastSeen) / (1000 * 60);
  
  if (minutesSince < 2) return 'Very High';
  if (minutesSince < 5) return 'High';
  if (minutesSince < 15) return 'Medium-High';
  if (minutesSince < 30) return 'Medium';
  if (minutesSince < 60) return 'Low';
  return 'None';
}

/**
 * Format capability value with appropriate icon and unit
 */
function formatCapabilityValue(capability) {
  const { name, value, unit, type, array_value } = capability;
  
  // Handle GPS/location data
  if (type === 'gps' && array_value && array_value.length >= 2) {
    const [lat, lon] = array_value;
    return `📍 ${lat.toFixed(3)},${lon.toFixed(3)}`;
  }
  
  // Handle different sensor types with appropriate icons
  const icons = {
    temperature: '🌡️',
    humidity: '💧',
    pressure: '📊',
    battery: '🔋',
    voltage: '⚡',
    current: '🔌',
    power: '💡',
    speed: '🏃',
    distance: '📏',
    acceleration: '🚀',
    gyroscope: '🌀',
    magnetometer: '🧭',
    light: '☀️',
    sound: '🔊',
    motion: '👋',
    door: '🚪',
    window: '🪟',
    switch: '🔘',
    button: '⏺️',
    weight: '⚖️',
    level: '📏',
    ph: '🧪',
    co2: '🌫️',
    lux: '💡',
    rssi: '📶',
    snr: '📡'
  };
  
  // Try to match type or name to get appropriate icon
  const icon = icons[type?.toLowerCase()] || 
               icons[name?.toLowerCase()] || 
               '📊'; // Default icon
  
  // Format the value
  if (value !== null && value !== undefined) {
    const formattedValue = typeof value === 'number' ? 
      (value % 1 === 0 ? value : value.toFixed(1)) : 
      value;
    return `${icon} ${formattedValue}${unit || ''}`;
  }
  
  return `${icon} --`;
}

export default function DeviceDataTable({ devices }) {
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-full">
        <TableHead>
          <TableRow className="border-b border-tremor-border">
            <TableHeaderCell className="text-left min-w-[200px]">
              Device
            </TableHeaderCell>
            <TableHeaderCell className="text-left min-w-[100px]">
              Status
            </TableHeaderCell>
            <TableHeaderCell className="text-left min-w-[300px]">
              Latest Values
            </TableHeaderCell>
            <TableHeaderCell className="text-left min-w-[150px] hidden lg:table-cell">
              Activity
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => {
            const status = getDeviceStatus(device.lastOnline, device.type);
            const activity = getActivityIndicator(device.lastOnline);
            const activityLevel = getActivityLevel(device.lastOnline);
            
            return (
              <TableRow key={device._id} className="hover:bg-gray-50">
                {/* Device Info */}
                <TableCell className="py-4">
                  <Link
                    to={`/devices/${device._id}`}
                    className="block hover:underline hover:underline-offset-4"
                  >
                    <div className="flex flex-col">
                      <span className="font-semibold text-tremor-content-strong">
                        {device.name}
                      </span>
                      <span className="text-sm text-tremor-content-subtle">
                        {device.serial}
                      </span>
                      <Badge 
                        className="mt-1 w-fit" 
                        size="xs"
                        color={device.type === 'mqtt' ? 'blue' : 'purple'}
                      >
                        {device.type === 'mqtt' ? 'MQTT' : 'LoRaWAN'}
                      </Badge>
                    </div>
                  </Link>
                </TableCell>
                
                {/* Status */}
                <TableCell className="py-4">
                  <div className="flex flex-col gap-1">
                    <Badge size="xs" color={status.color}>
                      {status.text}
                    </Badge>
                    <Text className="text-xs text-tremor-content-subtle">
                      {device.lastOnline ? toFriendlyTime(device.lastOnline) : 'Never seen'}
                    </Text>
                  </div>
                </TableCell>
                
                {/* Latest Values */}
                <TableCell className="py-4">
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {device.capabilities && device.capabilities.length > 0 ? (
                      device.capabilities.slice(0, 4).map((capability, index) => (
                        <Badge
                          key={`${capability.channel}-${index}`}
                          className="text-xs"
                          variant="neutral"
                          size="sm"
                        >
                          {formatCapabilityValue(capability)}
                        </Badge>
                      ))
                    ) : (
                      <Text className="text-sm text-tremor-content-subtle italic">
                        No data
                      </Text>
                    )}
                    {device.capabilities && device.capabilities.length > 4 && (
                      <Badge variant="neutral" size="sm" className="text-xs">
                        +{device.capabilities.length - 4} more
                      </Badge>
                    )}
                  </div>
                  
                  {/* Show activity on mobile since it's hidden in the separate column */}
                  <div className="mt-2 lg:hidden">
                    <div className="font-mono text-xs tracking-wider text-tremor-content-subtle">
                      {getActivityIndicator(device.lastOnline)} <span className="ml-1">{getActivityLevel(device.lastOnline)}</span>
                    </div>
                  </div>
                </TableCell>
                
                {/* Activity Indicator */}
                <TableCell className="py-4 hidden lg:table-cell">
                  <div className="flex flex-col gap-1">
                    <div className="font-mono text-sm tracking-wider">
                      {activity}
                    </div>
                    <Text className="text-xs text-tremor-content-subtle">
                      {activityLevel}
                    </Text>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}